export type TaskTimeScale = 'instant' | 'async' | 'long_running';
export type TaskState = 'queued' | 'processing' | 'awaiting_callback' | 'completed' | 'failed' | 'timed_out';
export type WebhookEvent = 'task.started' | 'task.progress' | 'task.completed' | 'task.failed' | 'task.timed_out';

export interface WebhookConfig {
  id: string;
  url: string;
  events: WebhookEvent[];
  secret: string;
  active: boolean;
  retries: number;
  timeout_ms: number;
}

export interface AsyncTask {
  id: string;
  op: string;
  time_scale: TaskTimeScale;
  state: TaskState;
  progress: number;
  created_at: number;
  started_at: number | null;
  completed_at: number | null;
  estimated_duration_ms: number;
  webhook_id: string | null;
  result: unknown;
  error: string | null;
}

export interface TaskQueueStats {
  queued: number;
  processing: number;
  awaiting_callback: number;
  completed_today: number;
  failed_today: number;
  avg_duration_ms: number;
}

const WEBHOOK_STUBS: WebhookConfig[] = [
  {
    id: 'wh_catalog_sync',
    url: 'https://hooks.mediablox.io/catalog/sync',
    events: ['task.completed', 'task.failed'],
    secret: 'whsec_***',
    active: true,
    retries: 3,
    timeout_ms: 30000,
  },
  {
    id: 'wh_distribution',
    url: 'https://hooks.mediablox.io/distribution/status',
    events: ['task.started', 'task.progress', 'task.completed', 'task.failed'],
    secret: 'whsec_***',
    active: true,
    retries: 5,
    timeout_ms: 60000,
  },
  {
    id: 'wh_rights_check',
    url: 'https://hooks.mediablox.io/rights/verify',
    events: ['task.completed', 'task.failed', 'task.timed_out'],
    secret: 'whsec_***',
    active: false,
    retries: 2,
    timeout_ms: 120000,
  },
  {
    id: 'wh_transcription',
    url: 'https://hooks.mediablox.io/ai/transcription',
    events: ['task.progress', 'task.completed'],
    secret: 'whsec_***',
    active: true,
    retries: 3,
    timeout_ms: 300000,
  },
];

const TASK_TEMPLATES: Record<string, { time_scale: TaskTimeScale; estimated_duration_ms: number; webhook_id: string | null }> = {
  'catalog.approve': { time_scale: 'instant', estimated_duration_ms: 200, webhook_id: null },
  'catalog.publish': { time_scale: 'async', estimated_duration_ms: 15000, webhook_id: 'wh_distribution' },
  'catalog.batch_delete': { time_scale: 'instant', estimated_duration_ms: 500, webhook_id: null },
  'catalog.review': { time_scale: 'instant', estimated_duration_ms: 100, webhook_id: null },
  'catalog.export': { time_scale: 'async', estimated_duration_ms: 30000, webhook_id: 'wh_catalog_sync' },
  'catalog.tag': { time_scale: 'instant', estimated_duration_ms: 300, webhook_id: null },
  'catalog.align': { time_scale: 'long_running', estimated_duration_ms: 180000, webhook_id: 'wh_transcription' },
  'rights.verify': { time_scale: 'long_running', estimated_duration_ms: 120000, webhook_id: 'wh_rights_check' },
  'distribution.deliver': { time_scale: 'long_running', estimated_duration_ms: 600000, webhook_id: 'wh_distribution' },
};

let taskQueue: AsyncTask[] = [];

export function enqueueTask(op: string, targets: string[]): AsyncTask {
  const template = TASK_TEMPLATES[op] ?? { time_scale: 'instant' as TaskTimeScale, estimated_duration_ms: 500, webhook_id: null };
  const task: AsyncTask = {
    id: `task_${crypto.randomUUID().slice(0, 8)}`,
    op,
    time_scale: template.time_scale,
    state: 'queued',
    progress: 0,
    created_at: Date.now(),
    started_at: null,
    completed_at: null,
    estimated_duration_ms: template.estimated_duration_ms * Math.max(1, targets.length * 0.1),
    webhook_id: template.webhook_id,
    result: null,
    error: null,
  };
  taskQueue = [...taskQueue, task];
  simulateExecution(task);
  return task;
}

function simulateExecution(task: AsyncTask) {
  const startDelay = task.time_scale === 'instant' ? 50 : 200;
  setTimeout(() => {
    task.state = 'processing';
    task.started_at = Date.now();
    task.progress = 10;

    if (task.time_scale === 'instant') {
      setTimeout(() => {
        task.state = 'completed';
        task.completed_at = Date.now();
        task.progress = 100;
      }, task.estimated_duration_ms);
    } else {
      const steps = task.time_scale === 'async' ? 4 : 8;
      let step = 0;
      const interval = setInterval(() => {
        step++;
        task.progress = Math.min(90, Math.round((step / steps) * 90));
        if (step >= steps) {
          clearInterval(interval);
          if (task.webhook_id) {
            task.state = 'awaiting_callback';
            setTimeout(() => {
              task.state = 'completed';
              task.completed_at = Date.now();
              task.progress = 100;
            }, 2000);
          } else {
            task.state = 'completed';
            task.completed_at = Date.now();
            task.progress = 100;
          }
        }
      }, task.estimated_duration_ms / steps / 10);
    }
  }, startDelay);
}

export function getTaskQueue(): AsyncTask[] {
  return [...taskQueue];
}

export function getQueueStats(): TaskQueueStats {
  const now = Date.now();
  const today = taskQueue.filter(t => now - t.created_at < 86400000);
  const completed = today.filter(t => t.state === 'completed');
  return {
    queued: taskQueue.filter(t => t.state === 'queued').length,
    processing: taskQueue.filter(t => t.state === 'processing').length,
    awaiting_callback: taskQueue.filter(t => t.state === 'awaiting_callback').length,
    completed_today: completed.length,
    failed_today: today.filter(t => t.state === 'failed').length,
    avg_duration_ms: completed.length > 0
      ? Math.round(completed.reduce((sum, t) => sum + ((t.completed_at ?? 0) - (t.started_at ?? 0)), 0) / completed.length)
      : 0,
  };
}

export function getWebhooks(): WebhookConfig[] {
  return [...WEBHOOK_STUBS];
}

export function describeWebhook(id: string): string | null {
  const wh = WEBHOOK_STUBS.find(w => w.id === id);
  if (!wh) return null;
  return [
    `Webhook: ${wh.id}`,
    `URL: ${wh.url}`,
    `Events: ${wh.events.join(', ')}`,
    `Active: ${wh.active} | Retries: ${wh.retries} | Timeout: ${wh.timeout_ms}ms`,
  ].join('\n');
}

export function describeTaskQueue(): string {
  const stats = getQueueStats();
  const recent = taskQueue.slice(-5).reverse();
  const lines = [
    '=== Task Queue ===',
    `Queued: ${stats.queued} | Processing: ${stats.processing} | Awaiting callback: ${stats.awaiting_callback}`,
    `Today: ${stats.completed_today} completed, ${stats.failed_today} failed | Avg: ${stats.avg_duration_ms}ms`,
    '',
    'Recent tasks:',
    ...recent.map(t => `  ${t.id} [${t.state}] ${t.op} (${t.time_scale}) ${t.progress}%`),
    '',
    'Webhooks:',
    ...WEBHOOK_STUBS.map(w => `  ${w.id} ${w.active ? 'ACTIVE' : 'INACTIVE'} -> ${w.events.length} events`),
  ];
  return lines.join('\n');
}

export function getTimeScaleLabel(scale: TaskTimeScale): string {
  switch (scale) {
    case 'instant': return '< 1s';
    case 'async': return '15-60s';
    case 'long_running': return '2-10min';
  }
}
